// ============================================================================
// BulkChallanGenerationPage.jsx
// Full-page bulk challan generation for a class/section.
// Shows all students, pending previous fees, next-month details, and allows edit.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { alertDialog } from "../../../utils/dialogs";
import { assignFeeToStudent, consolidateSameMonthUnpaidFees, deleteFeeEntry, updateDueDate } from "./feeApi";
import { bulkGenerateChallans, challansByFeeKey, updateGeneratedChallan } from "./challanApi";
import { FEE_CATEGORIES } from "./feeConstants";
import {
  buildCategoryInfo,
  buildComponentsFromCategories,
  buildEditStateFromRow,
  buildPendingItems,
  buildEligibleBulkMonthOptions,
  buildStudentBulkRow,
  calcRowNetPayable,
  fmtClass,
  fmtMonthLabel,
  fmtPKR,
  fmtSection,
  getEligibleBulkMonthsForStudent,
  resolveDefaultBulkMonth,
  savePreviousDuesForStudent,
} from "./feeChallanHelpers";

const C = {
  navy: "#0f1d36",
  blue: "#2a4a7f",
  blueSoft: "rgba(30,58,95,0.10)",
  amber: "#8a6c1f",
  amberSoft: "rgba(196,154,42,0.15)",
  green: "#2e7d32",
  greenSoft: "rgba(46,125,50,0.10)",
  red: "#c41e24",
  redSoft: "rgba(196,30,36,0.08)",
  slate: "#5a5e6a",
  border: "#e8e9ed",
  bg: "#f8f9fc",
  white: "#ffffff",
  text: "#1a1d23",
};

const S = {
  card: { background: C.white, borderRadius: "12px", border: `1px solid ${C.border}`, marginBottom: "16px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" },
  head: { padding: "18px 22px", background: C.navy, color: "#fff" },
  th: { textAlign: "left", padding: "10px 14px", fontSize: "11px", fontWeight: 700, color: C.slate, background: C.bg, borderBottom: `2px solid ${C.border}`, textTransform: "uppercase", whiteSpace: "nowrap" },
  td: { padding: "12px 14px", fontSize: "13px", borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  badge: (bg, fg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700, background: bg, color: fg }),
  btn: (bg, fg, border) => ({ padding: "9px 16px", borderRadius: "8px", border: border || "none", background: bg, color: fg, fontSize: "13px", fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" }),
  input: { padding: "7px 10px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", width: "100%", boxSizing: "border-box" },
  select: { padding: "7px 10px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", background: C.white },
};

const BulkChallanGenerationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { students = [], classFilter, sectionFilter, returnPath = "/fees" } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [feeAssignments, setFeeAssignments] = useState({});
  const [rows, setRows] = useState([]);
  const [challanMap, setChallanMap] = useState(new Map());
  const [summary, setSummary] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [bulkEdit, setBulkEdit] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);
  const [bulkMonth, setBulkMonth] = useState("");

  const monthOpts = useMemo(
    () => buildEligibleBulkMonthOptions(students, feeAssignments, challanMap),
    [students, feeAssignments, challanMap]
  );

  const editMonthOpts = useMemo(() => {
    if (!editRow) return monthOpts;
    const eligible = getEligibleBulkMonthsForStudent(editRow.student, feeAssignments, challanMap);
    const values = new Set(eligible);
    if (editRow.targetMonth) values.add(editRow.targetMonth);
    return [...values].sort().map((value) => ({ value, label: fmtMonthLabel(value) }));
  }, [editRow, feeAssignments, challanMap, monthOpts]);

  const refreshChallans = useCallback(async () => {
    setChallanMap(await challansByFeeKey());
  }, []);

  const buildRows = useCallback((studentList, fees, map = new Map()) => {
    return studentList.map((student) => {
      const base = buildStudentBulkRow(student, fees, map);
      return {
        id: String(student.id),
        ...base,
        challan: null,
        genStatus: "pending",
        genMessage: "",
      };
    });
  }, []);

  useEffect(() => {
    if (!students?.length || !classFilter) {
      navigate(returnPath || "/fees", { replace: true });
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const fees = await consolidateSameMonthUnpaidFees();
        const map = await challansByFeeKey();
        setFeeAssignments(fees);
        setChallanMap(map);
        const initialRows = buildRows(students, fees, map);
        const { options, defaultMonth } = resolveDefaultBulkMonth(students, fees, map, initialRows);
        const syncedRows = defaultMonth
          ? initialRows.map((row) => (
            row.targetMonth === defaultMonth
              ? row
              : { ...row, targetMonth: defaultMonth, sourceMode: "selected_month" }
          ))
          : initialRows;
        setRows(syncedRows);
        setBulkMonth(defaultMonth);
      } finally {
        setLoading(false);
      }
    })();
  }, [students, classFilter, returnPath, navigate, buildRows, refreshChallans]);

  const attachChallans = useCallback((rowList, map) => {
    return rowList.map((row) => {
      if (!row.targetMonth || !row.categoryId) return row;
      const key = `${row.categoryId}_${row.targetMonth}`;
      const challan = map.get(`${row.student.id}|${key}`) || null;
      return {
        ...row,
        challan,
        genStatus: challan ? "generated" : row.genStatus,
        genMessage: challan ? challan.challanNumber : row.genMessage,
      };
    });
  }, []);

  useEffect(() => {
    if (!rows.length) return;
    setRows((prev) => attachChallans(prev, challanMap));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challanMap]);

  useEffect(() => {
    if (!monthOpts.length) return;
    if (bulkMonth && monthOpts.some((o) => o.value === bulkMonth)) return;
    const nextMonth = monthOpts[0].value;
    setBulkMonth(nextMonth);
    setRows((prev) => prev.map((row) => (
      row.targetMonth === nextMonth
        ? row
        : { ...row, targetMonth: nextMonth, sourceMode: "selected_month" }
    )));
  }, [monthOpts, bulkMonth]);

  const runGeneration = useCallback(async ({ announce = false } = {}) => {
    if (!rows.length) return;
    setGenerating(true);
    setSuccessBanner(null);
    const generated = [];
    const skipped = [];
    const errors = [];
    const updatedFees = { ...feeAssignments };

    for (const row of rows) {
      try {
        if (row.sourceMode === "already_paid") {
          skipped.push({ student: row.student.name, reason: "Next month already paid" });
          continue;
        }
        if (!row.targetMonth || !row.selectedCategories?.length || !row.components?.length) {
          skipped.push({ student: row.student.name, reason: "No fee template available" });
          continue;
        }

        const catInfo = buildCategoryInfo(row.selectedCategories);
        const categoryId = row.selectedCategories.length > 1 ? "custom" : row.selectedCategories[0];
        const generatedComponents = buildGeneratedComponents(row);
        let feeStructure = null;

        const sid = String(row.student.id);
        const existingFee = Object.values(updatedFees[sid] || {}).find((f) => f.month === row.targetMonth);

        if (existingFee && existingFee.status !== "Paid" && existingFee.status !== "Waived") {
          feeStructure = { ...existingFee, components: generatedComponents };
        } else if (row.sourceMode === "next_month" || row.sourceMode === "existing_next" || row.sourceMode === "selected_month") {
          const payload = {
            studentId: row.student.id,
            studentName: row.student.name,
            studentClass: row.student.class,
            studentRollNo: row.student.rollNo,
            studentSection: row.student.section,
            categoryId,
            categoryLabel: row.categoryLabel || catInfo.label,
            categoryPrefix: row.categoryPrefix || catInfo.prefix,
            selectedCategories: [...row.selectedCategories],
            components: generatedComponents,
            academicYear: row.academicYear,
            month: row.targetMonth,
            dueDate: String(row.dueDate),
            concession: null,
          };
          feeStructure = await assignFeeToStudent(payload);
          if (!updatedFees[sid]) updatedFees[sid] = {};
          updatedFees[sid][`${feeStructure.categoryId}_${feeStructure.month}`] = feeStructure;
        } else {
          skipped.push({ student: row.student.name, reason: "Could not resolve fee entry" });
          continue;
        }

        if (feeStructure.status === "Paid" || feeStructure.status === "Waived") {
          skipped.push({ student: row.student.name, reason: "Fee already paid" });
          continue;
        }

        const result = await bulkGenerateChallans(
          [{ student: row.student, feeStructure }],
          { classFilter, sectionFilter }
        );

        if (result.generated.length) {
          generated.push(...result.generated);
        } else if (result.skipped.length) {
          skipped.push(...result.skipped);
        } else if (result.errors.length) {
          errors.push(...result.errors);
        }
      } catch (e) {
        errors.push({ student: row.student.name, reason: e.message || "Failed" });
      }
    }

    setFeeAssignments(updatedFees);
    await refreshChallans();

    const resultSummary = {
      total: rows.length,
      generated: generated.length,
      skipped: skipped.length,
      errors: errors.length,
      skippedList: skipped,
      errorList: errors,
    };
    setSummary(resultSummary);

    if (announce) {
      const msg = generated.length > 0
        ? `Successfully generated ${generated.length} challan${generated.length === 1 ? "" : "s"} for Class ${fmtClass(classFilter)}.${skipped.length ? ` ${skipped.length} skipped.` : ""}${errors.length ? ` ${errors.length} error(s).` : ""}`
        : `No new challans generated.${skipped.length ? ` ${skipped.length} skipped.` : ""}${errors.length ? ` ${errors.length} error(s).` : ""}`;

      setSuccessBanner({
        type: generated.length > 0 ? "success" : "warning",
        message: msg,
      });

      await alertDialog(msg, {
        title: generated.length > 0 ? "Challans Generated Successfully" : "Generation Complete",
        variant: generated.length > 0 ? "success" : "info",
      });
    }

    setGenerating(false);
  }, [rows, feeAssignments, classFilter, sectionFilter, refreshChallans]);

  const updateEditNet = (draft) => {
    const netPayable = calcRowNetPayable(draft.components, draft.pendingItems, draft.previousDuesAmount);
    return { ...draft, netPayable, subtotal: (draft.components || []).reduce((s, c) => s + (Number(c.amount) || 0), 0) };
  };

  const buildGeneratedComponents = (draft) => {
    const components = (draft.components || []).map((c) => ({ ...c }));
    const carryForward = (draft.pendingItems || [])
      .filter((item) => item.included !== false && item.month !== draft.targetMonth)
      .map((item, idx) => ({
        componentId: `carry_${idx}_${item.id}`,
        componentLabel: item.label,
        amount: Number(item.amount) || 0,
      }));
    if (draft.previousDuesAmount > 0) {
      carryForward.push({
        componentId: "previous_dues",
        componentLabel: "Previous Dues",
        amount: Number(draft.previousDuesAmount) || 0,
      });
    }
    return [...components, ...carryForward];
  };

  const persistRowEdit = async (draft) => {
    if (!draft.selectedCategories?.length) {
      await alertDialog("Select at least one fee category.", { title: "Missing Category" });
      return null;
    }
    if (!draft.components?.length) {
      await alertDialog("Add at least one fee component.", { title: "Missing Components" });
      return null;
    }

    const catInfo = buildCategoryInfo(draft.selectedCategories);
    const categoryId = draft.selectedCategories.length > 1 ? "custom" : draft.selectedCategories[0];
    const netPayable = calcRowNetPayable(draft.components, draft.pendingItems, draft.previousDuesAmount);
    const generatedComponents = buildGeneratedComponents(draft);
    const subtotal = generatedComponents.reduce((s, c) => s + (Number(c.amount) || 0), 0);

    const updatedRow = {
      ...draft,
      categoryId,
      categoryLabel: catInfo.label,
      categoryPrefix: catInfo.prefix,
      netPayable,
      subtotal,
      previousDues: Number(draft.previousDuesAmount) || 0,
    };

    if (draft.categoryId && draft.targetMonth) {
      await deleteFeeEntry(draft.student.id, draft.categoryId, draft.targetMonth);
    }

    await assignFeeToStudent({
      studentId: draft.student.id,
      studentName: draft.student.name,
      studentClass: draft.student.class,
      studentRollNo: draft.student.rollNo,
      studentSection: draft.student.section,
      categoryId,
      categoryLabel: catInfo.label,
      categoryPrefix: catInfo.prefix,
      selectedCategories: [...draft.selectedCategories],
      components: generatedComponents,
      academicYear: draft.academicYear,
      month: draft.targetMonth,
      dueDate: String(draft.dueDate),
      concession: null,
    });

    for (const item of (draft.pendingItems || []).filter((p) => p.type === "unpaid_fee" && p.included !== false)) {
      const f = item.fee;
      if (!f) continue;
      const origNet = Math.max(0, (f.components || []).reduce((s, c) => s + (c.amount || 0), 0) - (f.concessionAmount || 0));
      const newAmount = Number(item.amount) || 0;
      if (origNet <= 0 || newAmount === origNet) continue;
      const ratio = newAmount / origNet;
      const adjustedComponents = (f.components || []).map((c) => ({
        ...c,
        amount: Math.round((c.amount || 0) * ratio),
      }));
      await assignFeeToStudent({
        studentId: draft.student.id,
        studentName: draft.student.name,
        studentClass: draft.student.class,
        studentRollNo: draft.student.rollNo,
        studentSection: draft.student.section,
        categoryId: f.categoryId,
        categoryLabel: f.categoryLabel,
        categoryPrefix: f.categoryPrefix || "FEE",
        selectedCategories: f.selectedCategories || [f.categoryId].filter(Boolean),
        components: adjustedComponents,
        academicYear: f.academicYear,
        month: f.month,
        dueDate: f.dueDate || "10",
        concession: f.concession || null,
      });
    }

    savePreviousDuesForStudent(draft.student.id, draft.previousDuesAmount);

    const challan = draft.challan
      || challanMap.get(`${draft.student.id}|${draft.categoryId}_${draft.targetMonth}`);
    if (challan) {
      await updateGeneratedChallan(challan.id, {
        feeAssignmentKey: `${categoryId}_${draft.targetMonth}`,
        categoryId,
        dueDate: String(draft.dueDate),
        selectedCategories: [...draft.selectedCategories],
        categoryLabel: catInfo.label,
        categoryPrefix: catInfo.prefix,
        month: draft.targetMonth,
        components: generatedComponents,
        subtotal,
        netPayable,
      });
    }

    await updateDueDate(draft.student.id, categoryId, draft.targetMonth, String(draft.dueDate));
    return updatedRow;
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const updatedRow = await persistRowEdit(editRow);
    if (!updatedRow) return;

    setRows((prev) => prev.map((r) => (r.id === editRow.id ? updatedRow : r)));
    await refreshChallans();
    setEditRow(null);
    await alertDialog("Challan details updated successfully.", { title: "Saved", variant: "success" });
  };

  const saveBulkEdit = async () => {
    if (!bulkEdit) return;
    const updated = [];
    for (const row of rows) {
      const merged = updateEditNet({
        ...buildEditStateFromRow(row),
        targetMonth: bulkEdit.targetMonth,
        dueDate: bulkEdit.dueDate,
        selectedCategories: [...bulkEdit.selectedCategories],
        categoryLabel: bulkEdit.categoryLabel,
        categoryPrefix: bulkEdit.categoryPrefix,
        components: bulkEdit.components.map((c) => ({ ...c })),
      });
      const saved = await persistRowEdit(merged);
      if (saved) updated.push(saved);
    }
    setRows((prev) => prev.map((row) => updated.find((u) => u.id === row.id) || row));
    await refreshChallans();
    setBulkEdit(null);
    await alertDialog(`Bulk edit applied to ${updated.length} student${updated.length === 1 ? "" : "s"}.`, { title: "Bulk Edit Saved", variant: "success" });
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: C.slate }}>Loading bulk challan details…</div>;
  }

  const generatedCount = rows.filter((r) => r.challan || r.genStatus === "generated").length;

  return (
    <div style={{ padding: "20px 24px", maxWidth: "1400px", margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate(returnPath)} style={S.btn(C.bg, C.blue, `1px solid ${C.border}`)}>← Back to Challan Generation</button>
      </div>

      <div style={S.card}>
        <div style={S.head}>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "20px", fontWeight: 700 }}>Bulk Challan Generation</div>
          <div style={{ fontSize: "13px", opacity: 0.8, marginTop: 4 }}>
            Class {fmtClass(classFilter)}{sectionFilter ? ` · Section ${sectionFilter}` : ""} · {rows.length} students
          </div>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", borderBottom: `1px solid ${C.border}` }}>
          <select
            value={bulkMonth}
            onChange={(e) => {
              const nextMonth = e.target.value;
              setBulkMonth(nextMonth);
              setRows((prev) => prev.map((row) => ({ ...row, targetMonth: nextMonth, sourceMode: "selected_month" })));
            }}
            style={{ ...S.select, minWidth: 170 }}
            disabled={!monthOpts.length}
          >
            {monthOpts.length === 0 ? (
              <option value="">No eligible months</option>
            ) : (
              monthOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)
            )}
          </select>
          <button onClick={() => runGeneration({ announce: true })} disabled={generating} style={S.btn(C.navy, "#fff")}>
            {generating ? "Generating…" : `Generate All Challans (${rows.length})`}
          </button>
          <button
            onClick={() => {
              const base = rows[0] ? buildEditStateFromRow(rows[0]) : null;
              if (!base) return;
              setBulkEdit({
                targetMonth: base.targetMonth,
                dueDate: base.dueDate,
                selectedCategories: [...base.selectedCategories],
                categoryLabel: base.categoryLabel,
                categoryPrefix: base.categoryPrefix,
                components: (base.components || []).map((c) => ({ ...c })),
                netPayable: base.components.reduce((s, c) => s + (Number(c.amount) || 0), 0),
              });
            }}
            style={S.btn(C.bg, C.blue, `1px solid ${C.border}`)}
          >
            Edit All Students
          </button>
          <button onClick={() => navigate(returnPath, { state: { activeTab: "printing" } })} style={S.btn(C.green, "#fff")}>
            Go to Printing
          </button>
          <span style={{ marginLeft: "auto", fontSize: "12px", color: C.slate, fontWeight: 600 }}>
            {generatedCount} generated · {rows.filter((r) => r.unpaidFees?.length > 1).length} with extra pending
          </span>
        </div>

        {successBanner && (
          <div style={{
            padding: "14px 22px",
            background: successBanner.type === "success" ? C.greenSoft : C.amberSoft,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
            <span style={{ fontSize: 20 }}>{successBanner.type === "success" ? "✓" : "ℹ"}</span>
            <span style={{ fontSize: "14px", fontWeight: 700, color: successBanner.type === "success" ? C.green : C.amber }}>
              {successBanner.message}
            </span>
          </div>
        )}

        {summary && (
          <div style={{ padding: "14px 22px", background: C.bg, borderBottom: `1px solid ${C.border}`, display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {[
              ["Generated", summary.generated, C.green],
              ["Skipped", summary.skipped, C.amber],
              ["Errors", summary.errors, C.red],
            ].map(([label, val, color]) => (
              <span key={label} style={{ fontSize: "13px", fontWeight: 700, color }}>
                {label}: {val}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...S.card, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
          <thead>
            <tr>
              <th style={S.th}>#</th>
              <th style={S.th}>Student</th>
              <th style={S.th}>Roll No.</th>
              <th style={S.th}>Section</th>
              <th style={S.th}>Previous Pending</th>
              <th style={S.th}>Target Month</th>
              <th style={S.th}>Fee Categories</th>
              <th style={S.th}>Due Date</th>
              <th style={S.th}>Net Payable</th>
              <th style={S.th}>Challan</th>
              <th style={S.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const challan = row.challan
                || challanMap.get(`${row.student.id}|${row.categoryId}_${row.targetMonth}`);
              const pendingItems = row.pendingItems || buildPendingItems(row.student, row.unpaidFees);
              const pendingLabels = pendingItems
                .filter((p) => p.included !== false)
                .map((p) => `${p.label} — ${fmtPKR(p.amount)}`);
              if (row.previousDues > 0) {
                pendingLabels.push(`Previous dues — ${fmtPKR(row.previousDues)}`);
              }
              const modeLabel = row.sourceMode === "next_month" ? "Next month"
                : row.sourceMode === "pending" ? "Pending fee"
                : row.sourceMode === "already_paid" ? "Paid"
                : "Existing";

              return (
                <tr key={row.id} style={{ background: idx % 2 === 0 ? C.white : C.bg }}>
                  <td style={S.td}>{idx + 1}</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{row.student.name}</td>
                  <td style={{ ...S.td, fontFamily: "monospace" }}>{row.student.rollNo}</td>
                  <td style={S.td}>{fmtSection(row.student.section)}</td>
                  <td style={S.td}>
                    {pendingLabels.length ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {pendingLabels.map((p, i) => (
                          <span key={i} style={S.badge(C.redSoft, C.red)}>{p}</span>
                        ))}
                        {row.netPayable > (row.subtotal || 0) && (
                          <span style={{ fontSize: "11px", color: C.red, fontWeight: 600 }}>
                            incl. pending in total
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: C.slate, fontSize: "12px" }}>None</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{fmtMonthLabel(row.targetMonth)}</div>
                    <span style={S.badge(C.blueSoft, C.blue)}>{modeLabel}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(C.amberSoft, C.amber)}>{row.categoryLabel || "—"}</span>
                  </td>
                  <td style={S.td}>Day {row.dueDate}</td>
                  <td style={S.td}>
                    <span style={S.badge(C.greenSoft, C.green)}>{fmtPKR(row.netPayable)}</span>
                  </td>
                  <td style={S.td}>
                    {challan ? (
                      <div>
                        <span style={S.badge(C.greenSoft, C.green)}>Generated</span>
                        <div style={{ fontSize: "10px", fontFamily: "monospace", marginTop: 4, color: C.slate }}>{challan.challanNumber}</div>
                      </div>
                    ) : (
                      <span style={S.badge(C.redSoft, C.red)}>Pending</span>
                    )}
                  </td>
                  <td style={S.td}>
                    <button
                      onClick={() => setEditRow(buildEditStateFromRow(row))}
                      style={{ ...S.btn(C.bg, C.blue, `1px solid ${C.border}`), padding: "5px 10px", fontSize: "12px" }}
                    >
                      ✎ Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editRow && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,29,54,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setEditRow(null)}>
          <div style={{ background: C.white, borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
            <div style={S.head}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Edit — {editRow.student.name}</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>Changes update net payable automatically</div>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Target Month</label>
                <select style={{ ...S.select, width: "100%", marginTop: 6 }} value={editRow.targetMonth}
                  onChange={(e) => setEditRow((p) => updateEditNet({ ...p, targetMonth: e.target.value }))}>
                  {editMonthOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Due Date (day)</label>
                <input type="number" min="1" max="31" style={{ ...S.input, marginTop: 6 }} value={editRow.dueDate}
                  onChange={(e) => setEditRow((p) => ({ ...p, dueDate: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Fee Categories</label>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {FEE_CATEGORIES.filter((c) => c.id !== "custom").map((cat) => {
                    const checked = editRow.selectedCategories.includes(cat.id);
                    return (
                      <label key={cat.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setEditRow((p) => {
                            const n = new Set(p.selectedCategories);
                            checked ? n.delete(cat.id) : n.add(cat.id);
                            const ids = [...n];
                            const info = buildCategoryInfo(ids);
                            const components = buildComponentsFromCategories(ids, p.components);
                            return updateEditNet({
                              ...p,
                              selectedCategories: ids,
                              categoryLabel: info.label,
                              categoryPrefix: info.prefix,
                              components,
                            });
                          })} />
                        {cat.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Fee Components</label>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  {(editRow.components || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: C.slate }}>Select categories to add components</span>
                  ) : editRow.components.map((comp, ci) => (
                    <div key={comp.componentId || ci} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{comp.componentLabel}</span>
                      <input
                        type="number"
                        min="0"
                        style={{ ...S.input, width: 120 }}
                        value={comp.amount}
                        onChange={(e) => setEditRow((p) => {
                          const components = p.components.map((c, i) => i === ci ? { ...c, amount: Number(e.target.value) || 0 } : c);
                          return updateEditNet({ ...p, components });
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Previous Pending</label>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  {(editRow.pendingItems || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: C.slate }}>No previous pending</span>
                  ) : editRow.pendingItems.map((item, pi) => (
                    <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <input
                        type="checkbox"
                        checked={item.included !== false}
                        onChange={(e) => setEditRow((p) => {
                          const pendingItems = p.pendingItems.map((it, i) => i === pi ? { ...it, included: e.target.checked } : it);
                          return updateEditNet({ ...p, pendingItems });
                        })}
                      />
                      <span style={{ flex: 1, fontSize: 12, minWidth: 140 }}>{item.label}</span>
                      <input
                        type="number"
                        min="0"
                        style={{ ...S.input, width: 120 }}
                        value={item.amount}
                        disabled={item.included === false}
                        onChange={(e) => setEditRow((p) => {
                          const pendingItems = p.pendingItems.map((it, i) => i === pi ? { ...it, amount: Number(e.target.value) || 0 } : it);
                          return updateEditNet({ ...p, pendingItems });
                        })}
                      />
                    </div>
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>Additional previous dues</span>
                    <input
                      type="number"
                      min="0"
                      style={{ ...S.input, width: 120 }}
                      value={editRow.previousDuesAmount || 0}
                      onChange={(e) => setEditRow((p) => updateEditNet({ ...p, previousDuesAmount: Number(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
              </div>

              <div style={{ background: C.greenSoft, borderRadius: 10, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Net Payable</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: C.green }}>{fmtPKR(editRow.netPayable)}</span>
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setEditRow(null)} style={S.btn(C.bg, C.slate, `1px solid ${C.border}`)}>Cancel</button>
              <button onClick={saveEdit} style={S.btn(C.navy, "#fff")}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {bulkEdit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,29,54,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setBulkEdit(null)}>
          <div style={{ background: C.white, borderRadius: 16, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
            <div style={S.head}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Bulk Edit All Students</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>These changes will apply to every student on this page</div>
            </div>
            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14, overflowY: "auto" }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Target Month</label>
                <select style={{ ...S.select, width: "100%", marginTop: 6 }} value={bulkEdit.targetMonth}
                  onChange={(e) => setBulkEdit((p) => ({ ...p, targetMonth: e.target.value }))}>
                  {monthOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Due Date (day)</label>
                <input type="number" min="1" max="31" style={{ ...S.input, marginTop: 6 }} value={bulkEdit.dueDate}
                  onChange={(e) => setBulkEdit((p) => ({ ...p, dueDate: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Fee Categories</label>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {FEE_CATEGORIES.filter((c) => c.id !== "custom").map((cat) => {
                    const checked = bulkEdit.selectedCategories.includes(cat.id);
                    return (
                      <label key={cat.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                        <input type="checkbox" checked={checked}
                          onChange={() => setBulkEdit((p) => {
                            const n = new Set(p.selectedCategories);
                            checked ? n.delete(cat.id) : n.add(cat.id);
                            const ids = [...n];
                            const info = buildCategoryInfo(ids);
                            const components = buildComponentsFromCategories(ids, p.components);
                            const netPayable = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);
                            return {
                              ...p,
                              selectedCategories: ids,
                              categoryLabel: info.label,
                              categoryPrefix: info.prefix,
                              components,
                              netPayable,
                            };
                          })} />
                        {cat.label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: "uppercase" }}>Fee Components</label>
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  {(bulkEdit.components || []).length === 0 ? (
                    <span style={{ fontSize: 12, color: C.slate }}>Select categories to add components</span>
                  ) : bulkEdit.components.map((comp, ci) => (
                    <div key={comp.componentId || ci} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ flex: 1, fontSize: 13 }}>{comp.componentLabel}</span>
                      <input
                        type="number"
                        min="0"
                        style={{ ...S.input, width: 120 }}
                        value={comp.amount}
                        onChange={(e) => setBulkEdit((p) => {
                          const components = p.components.map((c, i) => i === ci ? { ...c, amount: Number(e.target.value) || 0 } : c);
                          const netPayable = components.reduce((s, c) => s + (Number(c.amount) || 0), 0);
                          return { ...p, components, netPayable };
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: C.greenSoft, borderRadius: 10, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>Base Net Payable For All</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: C.green }}>{fmtPKR(bulkEdit.netPayable || 0)}</span>
              </div>
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setBulkEdit(null)} style={S.btn(C.bg, C.slate, `1px solid ${C.border}`)}>Cancel</button>
              <button onClick={saveBulkEdit} style={S.btn(C.navy, "#fff")}>Apply To All Students</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkChallanGenerationPage;
